import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
/* Models */
import { DealerInfoEntities } from 'client/data/models/dealer-info';
/* Utils */
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { toDisplayString } from 'site-modules/shared/utils/phone-format';
import { getFinancingContainerFields } from 'site-modules/financing/utils/financing-utils';
import { updateCreativeId } from 'site-modules/financing/utils/update-creative-id';
import { getShortcutWorkingHours } from 'site-modules/financing/utils/short-working-hours-mapping';
/* Constants */
import { DECLINED_ACTION_LIST, DECLINED_ACTION_LIST_PRE_VIN } from 'client/site-modules/financing/constants/financing';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { Link } from 'site-modules/shared/components/link/link';
import { Button } from 'reactstrap';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { INVENTORY_TYPES_LOWERCASE } from 'client/constants/inventory-types';

export function DeclinedScreenError({ vehicle = {}, dealerInfo = {}, isMatchingVIN = false, isPreVinFlow = false }) {
  const [firstName, setFirstName] = useState('');

  useEffect(() => {
    async function getFirstName() {
      setFirstName(get(await getFinancingContainerFields(), 'financingFields.personal.firstNamePrimary', ''));
    }
    getFirstName();
  }, []);

  const dealerName = get(vehicle, 'dealerInfo.name', get(dealerInfo, 'name', ''));
  const phone = get(
    vehicle,
    'dealerInfo.phoneNumbers.trackable',
    get(
      vehicle,
      'dealerInfo.phoneNumbers.ppTrackable',
      get(vehicle, 'dealerInfo.phoneNumbers.basicTrackable', get(vehicle, 'dealerInfo.phoneNumbers.basic', {}))
    )
  );
  const formattedPhone = toDisplayString(phone);
  const workHours = get(vehicle, 'dealerInfo.workHours', get(dealerInfo, 'workHours', {}));
  const dealerWorkHours = getShortcutWorkingHours(workHours, true);
  const bodyText = isPreVinFlow
    ? 'Capital One was not able to pre-qualify you, but there are many local dealers that can help you explore other financing options'
    : `Capital One was not able to pre-qualify you. ${dealerName} works with many lenders and may have financing options
        that could work for you.`;
  const actionList = isPreVinFlow ? DECLINED_ACTION_LIST_PRE_VIN : DECLINED_ACTION_LIST;

  return (
    <div data-tracking-parent={updateCreativeId({ isPreVinFlow, isMatchingVIN, trackingParent: 'declined' })}>
      <h4 className="size-20 mb-2 fw-normal">Hey {firstName}, let&apos;s find another route!</h4>
      <p className="mb-1_25">{bodyText}</p>
      <h5 className="fw-bold mb-1">
        {isPreVinFlow ? 'What should I do now?' : 'What might the dealer be able to do for me?'}
      </h5>
      <ul className="list-unstyled mb-0_75">
        {actionList.map(({ id, text, icon }) => (
          <li key={id} className="ms-1_5 mb-1_25">
            <img className="mx-md-auto mw-100" src={getStaticImageUrl(icon)} alt="" loading="lazy" width={34} />
            <ContentFragment tag="p" classes="ms-0_75 d-inline-block">
              {text}
            </ContentFragment>
          </li>
        ))}
      </ul>
      <div className="d-flex justify-content-center">
        {isPreVinFlow ? (
          <Button
            className="mt-1 text-capitalize size-16"
            color="primary"
            tag={Link}
            to={srpLinkBuilder({
              inventorytype: `${INVENTORY_TYPES_LOWERCASE.USED},${INVENTORY_TYPES_LOWERCASE.CPO}`,
            })}
          >
            See All For Sale
          </Button>
        ) : (
          <div className="info-wrapper px-2 py-1 bg-gray-extra-light mb-2 text-center">
            <div className="fw-bold size-16 mb-0_25">Call {dealerName}</div>
            <div className="size-20 fw-bold mb-0_75 ">
              <i className="icon-phone2 me-0_25 text-primary-darker size-16" aria-hidden />
              <a className="text-primary-darker" href={`tel:${formattedPhone}`}>
                {formattedPhone}
              </a>
            </div>
            <ul className="list-unstyled mb-0" aria-label="Work schedule">
              {dealerWorkHours.map(dealerWorkHour => (
                <li key={dealerWorkHour} className="size-14">
                  {dealerWorkHour}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

DeclinedScreenError.propTypes = {
  vehicle: PropTypes.shape({}),
  dealerInfo: DealerInfoEntities.DealerInfo,
  isMatchingVIN: PropTypes.bool,
  isPreVinFlow: PropTypes.bool,
};
